import React from 'react';

import { FormattedMessage } from 'react-intl';

import { Flight, Hotel } from '@mui/icons-material';
import { CoreUtils, ShortCodeEnums } from '@nhl/nhl-breakaway-ui';

import CommonMessages from '~/core/commonMessages';
import { GameCenterIcon, PromotionIcon, TicketsIcon, WatchIcon } from '~/core/customIcons';
import { isValidLinkScheduleState, showGameCenterLink, showTicketsLink } from '~/core/utils';

import { GameLink, ScheduleGame } from '../../../../../types';

export type PlaneInformation = {
    planeLink: string | undefined;
    hasPlaneLink: boolean;
    planePartner: string | undefined;
};

export type HotelInformation = {
    hotelLink: string | undefined;
    hasHotelLink: boolean;
    hotelPartner: string | undefined;
};

export type PromoInformation = {
    promoLink: string | undefined;
    hasPromoLink: boolean;
    promoMessage: string | undefined;
};

export const getPlaneInformation = (isHomeGame: boolean, game?: ScheduleGame): PlaneInformation => {
    const planeLink = isHomeGame ? game?.homeTeam?.airlineLink : game?.awayTeam?.airlineLink;

    return {
        planeLink,
        hasPlaneLink: !!planeLink,
        planePartner: isHomeGame ? game?.homeTeam?.airlineDesc : game?.awayTeam?.airlineDesc,
    };
};

export const getHotelInformation = (isHomeGame: boolean, game?: ScheduleGame): HotelInformation => {
    const hotelLink = isHomeGame ? game?.homeTeam?.hotelLink : game?.awayTeam?.hotelLink;

    return {
        hotelLink,
        hasHotelLink: !!hotelLink,
        hotelPartner: isHomeGame ? game?.homeTeam?.hotelDesc : game?.awayTeam?.hotelDesc,
    };
};

export const getPromoInformation = (isHomeGame: boolean, game?: ScheduleGame): PromoInformation => {
    const promoLink = isHomeGame ? game?.homeTeam?.promoLink : game?.awayTeam?.promoLink;

    return {
        promoLink,
        hasPromoLink: !!promoLink,
        promoMessage: isHomeGame ? game?.homeTeam?.promoDesc : game?.awayTeam?.promoDesc,
    };
};

type ScoringLinkFlags = {
    isTicketsLinkShown: boolean;
    isPromoLinkShown: boolean;
    isPlaneLinkShown: boolean;
    isHotelLinkShown: boolean;
    isGameCenterLinkShown: boolean;
    isStreamLinkShown: boolean;
    isThreeMinuteRecapShown: boolean;
};

export const getScoringLinkFlags = (
    game: ScheduleGame,
    team: ShortCodeEnums.TeamTriCode,
    streamUrl?: string,
    threeMinuteRecap?: string
): ScoringLinkFlags => {
    const preGame = CoreUtils.isPregame(game.gameState) && isValidLinkScheduleState(game.gameScheduleState);
    const liveGame = CoreUtils.isGameLive(game.gameState);
    const postGame = CoreUtils.isPostGame(game.gameState);
    const isHomeGame = game?.homeTeam.abbrev === team;
    const isNeutralSite = game.neutralSite === true;
    const planeInformation = getPlaneInformation(isHomeGame, game);
    const hotelInformation = getHotelInformation(isHomeGame, game);
    const promoInformation = getPromoInformation(isHomeGame, game);
    return {
        isTicketsLinkShown: showTicketsLink(game) && (isHomeGame || isNeutralSite),
        isPromoLinkShown: promoInformation.promoMessage
            ? promoInformation.hasPromoLink && preGame && isHomeGame
            : false,
        isPlaneLinkShown: planeInformation.planePartner
            ? planeInformation.hasPlaneLink && preGame && !isNeutralSite
            : false,
        isHotelLinkShown: hotelInformation.hotelPartner
            ? hotelInformation.hasHotelLink && preGame && !isNeutralSite
            : false,
        isGameCenterLinkShown: showGameCenterLink(game),
        isStreamLinkShown: streamUrl ? liveGame : false,
        isThreeMinuteRecapShown: threeMinuteRecap ? postGame : false,
    };
};

export const getPopupLinks = (
    game: ScheduleGame,
    team: ShortCodeEnums.TeamTriCode,
    ticketsLink?: string,
    streamUrl?: string,
    threeMinuteRecap?: string
): Array<GameLink> => {
    const isHomeGame = game?.homeTeam.abbrev === team;
    const planeInformation = getPlaneInformation(isHomeGame, game);
    const hotelInformation = getHotelInformation(isHomeGame, game);
    const promoInformation = getPromoInformation(isHomeGame, game);
    const scoringLinkFlags = getScoringLinkFlags(game, team, streamUrl, threeMinuteRecap);
    return [
        {
            actionUrl: ticketsLink,
            icon: <TicketsIcon />,
            message: <FormattedMessage {...CommonMessages.linkDescriptions.tickets} />,
            show: scoringLinkFlags.isTicketsLinkShown,
        },
        {
            actionUrl: promoInformation.promoLink,
            icon: <PromotionIcon />,
            message: promoInformation.promoMessage,
            show: scoringLinkFlags.isPromoLinkShown,
        },
        {
            actionUrl: planeInformation.planeLink,
            icon: <Flight />,
            message: planeInformation.planePartner,
            show: scoringLinkFlags.isPlaneLinkShown,
        },
        {
            actionUrl: hotelInformation.hotelLink,
            icon: <Hotel />,
            message: hotelInformation.hotelPartner,
            show: scoringLinkFlags.isHotelLinkShown,
        },
        {
            actionUrl: game.gameCenterLink,
            icon: <GameCenterIcon />,
            message: <FormattedMessage {...CommonMessages.linkDescriptions.gamecenter} />,
            show: scoringLinkFlags.isGameCenterLinkShown,
        },
        {
            actionUrl: streamUrl,
            icon: <WatchIcon />,
            message: <FormattedMessage {...CommonMessages.linkDescriptions.stream} />,
            show: scoringLinkFlags.isStreamLinkShown,
        },
        {
            actionUrl: threeMinuteRecap,
            icon: <WatchIcon />,
            message: <FormattedMessage {...CommonMessages.linkDescriptions.recap} />,
            show: scoringLinkFlags.isThreeMinuteRecapShown,
        },
    ].filter((link) => link.show);
};
